import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Wrench, Clock, Car, DollarSign, Droplet, Zap, Truck, FileCheck, Settings, HelpCircle } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  wrench: Wrench, clock: Clock, car: Car, dollar: DollarSign, droplet: Droplet,
  zap: Zap, truck: Truck, filecheck: FileCheck, settings: Settings, helpcircle: HelpCircle,
};

const serviceBackgroundImages = {
  'Revisione Auto': 'https://customer-assets.emergentagent.com/job_94078927-4fa8-4ee7-b3bc-58586c625c92/artifacts/6t5tdgl3_revisione%201.jpeg',
  'Tagliando Auto': 'https://customer-assets.emergentagent.com/job_94078927-4fa8-4ee7-b3bc-58586c625c92/artifacts/1dnd3cgj_tagliando%201.jpg',
  'Riparazioni Meccaniche': 'https://customer-assets.emergentagent.com/job_94078927-4fa8-4ee7-b3bc-58586c625c92/artifacts/ndrvvokq_riparazione%20meccaniche%201.jpg',
  'Vendita Auto Nuove e Usate': 'https://customer-assets.emergentagent.com/job_94078927-4fa8-4ee7-b3bc-58586c625c92/artifacts/vi6876lr_vendita%20auto%20nuove%20e%20usate%201.jpg',
  'Noleggio Auto': 'https://customer-assets.emergentagent.com/job_94078927-4fa8-4ee7-b3bc-58586c625c92/artifacts/wwwt6voz_noleggio%20auto%201.jpg',
  'Soccorso Stradale 24h': 'https://customer-assets.emergentagent.com/job_94078927-4fa8-4ee7-b3bc-58586c625c92/artifacts/7yx2e5rx_soccorso%20stradale%201.jpg',
  'Revisione Motore': 'https://customer-assets.emergentagent.com/job_94078927-4fa8-4ee7-b3bc-58586c625c92/artifacts/3k2v9g48_messa%20a%20punto%20motore%201.jpeg',
  'Lavaggio e ripristino FAP/DPF': 'https://customer-assets.emergentagent.com/job_94078927-4fa8-4ee7-b3bc-58586c625c92/artifacts/1ucjl51i_fap%201.jpg',
  'Decarbonizzazione Motore': 'https://customer-assets.emergentagent.com/job_94078927-4fa8-4ee7-b3bc-58586c625c92/artifacts/gxq3isib_h2%20blaster%201.jpg',
  'Ricarica Aria Condizionata': 'https://customer-assets.emergentagent.com/job_94078927-4fa8-4ee7-b3bc-58586c625c92/artifacts/9b46mw1z_ricarica%20aria%20condizionata%201%20.jpg',
  'Riparazione Parabrezza Auto': 'https://customer-assets.emergentagent.com/job_94078927-4fa8-4ee7-b3bc-58586c625c92/artifacts/49vvw2m8_riparazione%20parabrezza%20auto%201.jpg',
  'Cambio Gomme ed Equilibratura': 'https://customer-assets.emergentagent.com/job_94078927-4fa8-4ee7-b3bc-58586c625c92/artifacts/jw31b7kx_cambio%20gomme%20ed%20equilibratura%201.jpg',
};

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const { lang, t, serviceName } = useLanguage();

  useEffect(() => { loadServices(); }, []);

  const loadServices = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error loading services:', error);
      toast.error('Errore nel caricamento dei servizi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-gray-50" data-testid="services-hero">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Space Grotesk' }}>{t('services', 'title')}</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t('services', 'subtitle')}</p>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8" data-testid="services-grid">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-12" data-testid="services-loading">
              <div className="inline-block w-12 h-12 border-4 border-[#0066b2] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : services.length === 0 ? (
            <div className="text-center py-12 text-gray-500" data-testid="services-empty">{t('services', 'empty')}</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, idx) => {
                const IconComponent = iconMap[service.icon] || Wrench;
                const backgroundImage = serviceBackgroundImages[service.name] || 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=600&h=400&fit=crop';
                return (
                  <Link key={service.id} to={`/servizi/${service.id}`}
                    className="group bg-white rounded-2xl border-2 border-gray-200 hover:border-[#0066b2] transition-all duration-300 hover:shadow-2xl transform hover:-translate-y-2 overflow-hidden flex flex-col"
                    data-testid={`service-card-${idx}`}>
                    <div className="h-56 bg-gray-50 flex items-center justify-center overflow-hidden">
                      <img src={backgroundImage} alt={service.name} className="w-full h-full object-contain" />
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="w-14 h-14 bg-gradient-to-br from-[#0066b2] to-[#005494] rounded-xl flex items-center justify-center mb-4 transform group-hover:rotate-6 transition-transform shadow-lg">
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk' }}>{serviceName(service.name)}</h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-3 flex-1">{lang === 'en' && service.description_en ? service.description_en : service.description}</p>
                      <div className="pt-3 border-t border-gray-200">
                        <span className="text-[#0066b2] font-medium text-sm group-hover:underline">{t('services', 'learnMore')}</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
